// @flow

import { capitalize } from 'lodash';

import { addParamsToUrl } from '@zapier/url-utils';
import { singular } from '@zapier/common-utils';

// The editor and platform differ on how they refer to these names
// In platform we have `triggers` and `creates`. The editor calls these `reads` and `writes`.
// The platformSchema type key is called an action in the editor.
// For example, if 'new_issue' is the trigger key in platform (triggers.new_issue.key),
// it is called the `action` in the editor (node.action: 'new_issue') even if it's a trigger.
const nodeTypeMap = {
  trigger: 'read',
  triggers: 'read',
  create: 'write',
  creates: 'write',
  search: 'search',
  searches: 'search',
};

/** Kind of a mishmash of platform and editor types. Trying to straddle both worlds.  */
export type ZapMeta = {
  /** Referring to a node's service in editor.  */
  service: {
    /** The implementation version  */
    version: string,
    /** The integration title */
    name: string,
  },
  /** The platformSchemaType key (`triggers[triggerName].key`) */
  action: string,
  /** The selected_api aka serviceKey. */
  selectedApi: string,
  /** The strange un-nameable thing that is a trigger, create, search etc. in the platform schema */
  platformSchemaType: $Keys<typeof nodeTypeMap>,
  /** The label for the above. In platform it would be trigger.display.label */
  actionLabel: string,
};

type OptionsType = {
  /** Used by team-editor to track usage of the auto-create link */
  referrer: string,
};

const defaultTriggerParams = {
  template__0__selected_api: 'ScheduleAPI',
  template__0__type_of: 'read',
  template__0__action: 'every_day',
};

function createZapTitle(zap) {
  const { actionLabel, platformSchemaType, service } = zap;
  const format = type => singular(capitalize(type));

  return `Test Zap for ${actionLabel} ${format(platformSchemaType)} from ${
    service.name
  } Version ${service.version}`;
}

/**
 * Creates a link that will auto-create a Zap in the Zap Editor.
 * If it's an action, we will add a trigger from Schedule by Zapier.
 */
function buildZapLink(zap: ZapMeta, options: OptionsType) {
  const { referrer } = options || {};
  const nodeType = nodeTypeMap[zap.platformSchemaType];
  // a platform trigger is a `node.type_of: 'read'` in the editor.
  const isTrigger = nodeType === 'read';
  const templateKey = `template__${isTrigger ? 0 : 1}`;

  const queryParams = {
    referrer,
    create: true,
    ...(!isTrigger ? defaultTriggerParams : {}),
    [`${templateKey}__selected_api`]: zap.selectedApi,
    [`${templateKey}__type_of`]: nodeType,
    [`${templateKey}__action`]: zap.action,
    template__0__title: createZapTitle(zap),
  };

  return addParamsToUrl('/app/editor', queryParams);
}

export { buildZapLink };
